import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { LoginModal } from "./LoginModal";
import { Modal } from "./Modal";
import { PageStatus } from "./PageStatus";
import { createPortal } from "preact/compat";
import { PageId, SyncState, WriteSheet } from "./types";
import Toast from "./Toast";
import * as Sentry from "@sentry/react";
import { trackEvent } from "./analytics";
import { SyncService } from "./syncService";

// only run in prod
if (document.location.hostname === "blank.page") {
  Sentry.init({
    dsn: "https://72f60a927c8555bcc4de92fa40fb6c1d@o4506088787410944.ingest.sentry.io/4506088789311488",
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /^https:\/\/blank.page/],
      }),
      new Sentry.Replay(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

function createChildElement(parent: HTMLElement) {
  const element = document.createElement("div");
  element.classList.add("tw");
  parent.appendChild(element);
  return element;
}

function SyncApp({
  supabase,
  writeSheet,
  pageId,
}: {
  supabase: SupabaseClient;
  writeSheet: WriteSheet;
  pageId: PageId;
}) {
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const { current: syncService } = useRef<SyncService>(
    new SyncService({
      supabase,
      writeSheet,
      pageId,
      onUpdated(newState) {
        setSyncState(newState);
      },
    })
  );
  const [syncState, setSyncState] = useState<SyncState>(syncService.state);

  const [showLogoutToast, setShowLogoutToast] = useState<boolean>(false);
  const pageStatusEl = document.getElementById("page-status");

  if (!pageStatusEl) {
    throw new Error("Could not find page status element");
  }

  useEffect(() => {
    const stop = syncService.start();

    return () => {
      stop();
    };
  }, []);

  useEffect(() => {
    const confirmUnload = (e: BeforeUnloadEvent) => {
      if (syncState?.type === "MODIFIED_PAGE") {
        e.preventDefault();
        e.returnValue = "Please wait a few seconds for the page to sync";
      }
    };

    window.addEventListener("beforeunload", confirmUnload);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", confirmUnload);
    };
  }, [syncState]);

  const hasMergeConflict = syncState?.type === "MERGE_CONFLICT";

  return (
    <>
      <Modal
        show={showLogoutModal}
        className="max-w-sm"
        title="Before you leave..."
        allowClose={true}
        content={
          <>
            <p className="leading-relaxed">
              To keep your content private, we will clear the page content after
              you log out. You can always sign in again to continue where you
              left off.
            </p>
            <div className="mt-8 flex">
              <button
                className="ml-auto bg-blue-600 text-white ml-auto py-2 px-5 rounded-lg text-md font-medium hover:cursor-pointer hover:bg-black"
                onClick={async () => {
                  await syncService.signOut();
                  setShowLogoutModal(false);
                  setShowLogoutToast(true);
                }}
              >
                I understand, log me out
              </button>
            </div>
          </>
        }
      />
      {hasMergeConflict && (
        <Modal
          show={true}
          className="max-w-sm"
          title="Heads up!"
          allowClose={false}
          content={
            <>
              <p className="leading-relaxed">
                Looks like you already have existing content in your account
                page. To avoid a possible conflict, we will merge all contents
                into the same page.
              </p>
              <div className="pt-6 flex">
                <a
                  type="submit"
                  rel="noreferrer"
                  target="_blank"
                  href="https://blank.page/cafe/channels/support"
                  className="ml-auto text-gray-500 py-2 pr-5 text-md font-medium hover:cursor-pointer"
                >
                  Need help?
                </a>
                <button
                  type="submit"
                  className="disabled:opacity-50 disabled:pointer-events-none ml-1 bg-blue-600 text-white py-2 px-5 rounded-lg text-md font-medium hover:cursor-pointer hover:bg-black"
                  onClick={() => {
                    syncService.acceptMergeResolution();
                  }}
                >
                  Merge and continue
                </button>
              </div>
            </>
          }
        />
      )}
      <LoginModal
        supabase={supabase}
        show={showLoginModal}
        onLogin={() => setShowLoginModal(false)}
        onClose={() => setShowLoginModal(false)}
      />
      <Toast
        message="Logged out successfully."
        show={showLogoutToast}
        onClose={() => setShowLogoutToast(false)}
        dissapearAfter={3000}
      />
      )
      {createPortal(
        <div>
          <PageStatus
            state={syncState}
            handleSignIn={() => {
              trackEvent("Sign in to save clicked");
              setShowLoginModal(true);
            }}
            handleSignOut={() => {
              setShowLogoutModal(true);
            }}
          />
        </div>,
        pageStatusEl
      )}
    </>
  );
}

function App() {
  const [showFirstModal, setShowFirstModal] = useState<boolean>(false);
  const [showSecondModal, setShowSecondModal] = useState<boolean>(false);

  useEffect(() => {
    if (localStorage.getItem("topbarClosed") !== "true") {
      document.body.classList.add("topbar-open");
    }

    const topbarCloseButton = document.getElementById("topbar-close-button");
    const topbarCta = document.getElementById("topbar-cta");

    if (topbarCloseButton) {
      topbarCloseButton.addEventListener("click", (e) => {
        e.preventDefault();
        document.body.classList.remove("topbar-open");
        localStorage.setItem("topbarClosed", "true");
        setShowFirstModal(true);
        trackEvent("SC Topbar Close Clicked");
      });
    }

    if (topbarCta) {
      topbarCta.addEventListener("click", () => {
        localStorage.setItem("topbarClosed", "true");
        document.body.classList.remove("topbar-open");
        trackEvent("SC Topbar CTA Clicked");
      });
    }
  }, []);

  return (
    <div>
      <Modal
        show={showFirstModal}
        onClose={() => setShowFirstModal(false)}
        className="w-[640px]"
        allowClose={false}
        title="Before you keep writing..."
        content={
          <>
            <p className="pt-2 leading-relaxed">
              This page is made possible by people like you. We believe the
              Internet deserves an open, beautiful, blank page to write what
              matters in our lives. Support us in keeping it this way with as
              little as $1.
            </p>
            <p className="pt-6 text-sm text-gray-500">
              Once or monthly, we’ll thank from the heart.
            </p>
            <div className="pt-6 mt-6 flex border-t dark:border-gray-800">
              <div className="flex gap-x-5 items-center">
                <a
                  onClick={() => {
                    trackEvent("SC First Modal CTA Clicked");
                    setShowFirstModal(false);
                  }}
                  href="https://www.buymeacoffee.com/blankpage"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#474B55] hover:bg-[#363840] py-2 px-4 rounded-lg text-white no-underline text-sm"
                >
                  Support
                </a>
                <div className="max-w-[120px]">
                  <a
                    onClick={() => {
                      trackEvent("SC First Modal BMCA Logo Clicked");
                      setShowFirstModal(false);
                    }}
                    href="https://www.buymeacoffee.com/blankpage"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img className="block dark:hidden" src="/img/bmc.svg" />
                    <img
                      className="hidden dark:block"
                      src="/img/bmc_dark.svg"
                    />
                  </a>
                </div>
              </div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowFirstModal(false);
                  setShowSecondModal(true);
                  trackEvent("SC First Modal Maybe Later Clicked");
                }}
                className="ml-auto text-gray-500 hover:text-gray-600 py-2 pr-5 text-sm font-medium hover:cursor-pointer"
              >
                Maybe later
              </a>
            </div>
          </>
        }
      />
      <Modal
        show={showSecondModal}
        onClose={() => {
          setShowSecondModal(false);
          trackEvent("SC Second Modal Close Clicked");
        }}
        className="max-w-xl"
        allowClose={true}
        title="There are more ways to support!"
        content={
          <>
            <p className="leading-relaxed">
              If you are unable to donate, you can still help us by taking a
              quick survey or by joining our community forum.
            </p>
            <p className="pt-6 text-sm text-gray-500">
              Thanks for your time 🙌
            </p>
            <div className="pt-6 mt-6 flex border-t dark:border-gray-800">
              <div className="flex gap-x-4 items-center">
                <a
                  onClick={() => {
                    setShowSecondModal(false);
                    trackEvent("SC Second Modal CTA Clicked");
                  }}
                  href="https://forms.reform.app/ZCfKMr/feedback-2024/ud25nv"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#474B55] hover:bg-[#363840] py-2 px-4 rounded-lg text-white no-underline text-sm"
                >
                  Take 1-min survey
                </a>
              </div>
            </div>
          </>
        }
      />
    </div>
  );
}

function loadSyncFeatureFlag(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const isInUrl = urlParams.has("sync");

  const syncFlag = localStorage.getItem("syncEnabled");
  const isInLocalStorage = syncFlag === "true";

  return isInUrl || isInLocalStorage;
}

const supabaseConfig: Record<string, { url: string; key: string }> = {
  development: {
    url: "https://uhkrtcirqzojuyybjkty.supabase.co",

    key: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVoa3J0Y2lycXpvanV5eWJqa3R5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTcyMTk4NjgsImV4cCI6MjAxMjc5NTg2OH0.32iCqvaPqQ2YR7GTKDbba3r3UCckY7fRaYDpze55vNM",
  },
  production: {
    url: "https://uhkrtcirqzojuyybjkty.supabase.co",

    key: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVoa3J0Y2lycXpvanV5eWJqa3R5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTcyMTk4NjgsImV4cCI6MjAxMjc5NTg2OH0.32iCqvaPqQ2YR7GTKDbba3r3UCckY7fRaYDpze55vNM",
  },
};

export async function init(writeSheet: WriteSheet) {
  // const env =
  //   document.location.hostname === "localhost" ? "development" : "production";
  //
  // const supabase = supabaseConfig[env];
  //
  // const supabaseClient = createClient(supabase.url, supabase.key, {
  //   global: { fetch: fetch.bind(globalThis) },
  // });
  //
  // const pageId: PageId =
  //   window.location.pathname === "/"
  //     ? null
  //     : window.location.pathname.split("/")[1];
  //
  // const isSyncEnabled = loadSyncFeatureFlag();
  // localStorage.setItem("syncEnabled", isSyncEnabled.toString());
  //
  // if (isSyncEnabled) {
  //   render(
  //     <SyncApp writeSheet={writeSheet} supabase={supabaseClient} pageId={pageId} />,
  //     createChildElement(document.body)
  //   );
  // }
  //
  render(<App />, createChildElement(document.body));
}
